/**
 * The symbol universe — group types, groups, and a group as a screener snapshot.
 *
 * Follows `compile-expr.ts`'s rules exactly, because they are the app's rules
 * for talking to a service: the base URL is read once at module load, a missing
 * one makes the feature *degrade* rather than error, and nothing here ever
 * throws — a caller gets `undefined` and renders a notice.
 *
 * `/sym/*` is served by qv-data, the same app as `/set/*`. `VITE_QV_SYM_URL`
 * exists so that stays sayable, but it falls back to `VITE_QV_SET_URL` — one
 * service, one origin, and nobody has to set two variables to the same value.
 */

import { notePassResponse } from "../lib/pass";
import {
  decodeColumnMeta,
  decodeScreenerFrames,
  type ScreenerColumnMeta,
  type ScreenerFrame,
} from "@qufolio/transport";

import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const SYM_URL = (
  httpBase(
    import.meta.env.VITE_QV_SYM_URL,
    import.meta.env.VITE_QV_SET_URL,
  ) ||
  ""
).replace(/\/$/, "");

/** True when a base URL is configured — the group bar hides itself otherwise. */
export const symbolsConfigured = SYM_URL !== "";

/** One entry in the picker's top level. */
export interface SymbolGroupType {
  groupType: string;
  label: string;
  groupCount: number;
}

export interface SymbolGroup {
  code: string;
  name: string;
  description: string | null;
  groupType: string;
  mechanism: string;
  memberCount: number;
  /**
   * Which market this group screens — `US`, `IN`.
   *
   * **The group is the market selector** (D40). A screener cannot mix markets:
   * `marketCap` is native currency, `close_spy` is the market's own benchmark,
   * and a cross-sectional rank across two markets is not a rank. So there is no
   * separate market control and no `market` field on `ScreenerConfig` — picking
   * `IN_NIFTY_50` *is* selecting India, and a second field could disagree with
   * this one.
   */
  market: string | null;
  /** Who publishes the membership: `nse` · `bse` · `fmp` · `qufolio`. */
  provider: string | null;
  /**
   * Whether this account's plan includes screening this universe.
   *
   * **Marked rather than omitted**, the same call `IndicatorEntry.locked` makes
   * and for the same reason: the group bar is where somebody learns that a
   * wider universe exists, and a list narrowed to `SP500` teaches that `SP500`
   * is all there is. The server sets it and refuses the screen separately.
   */
  locked?: boolean;
  /**
   * The picker's **second** level, under `groupType` — `broad` · `sector` ·
   * `thematic` · `strategy` · `cap` · `exchange`.
   */
  category: string | null;
  /**
   * ISO timestamp, or null when membership has never been resolved.
   *
   * Null is **not** "zero members" and must not render as such: an unbuilt
   * group and a genuinely empty one look identical without this.
   */
  resolvedAt: string | null;
}

export interface GroupSnapshot {
  frames: ScreenerFrame[];
  detail: ScreenerFrame | undefined;
  columns: ScreenerColumnMeta[];
}

/**
 * A screen fetch that either worked or says why it did not.
 *
 * The rest of this module answers `undefined` and lets the caller render a
 * notice, which is right for a catalog: a missing menu degrades to no menu. It
 * is wrong here, because this is the request that fills the grid, and every way
 * it can fail produced the same sentence — "no screen data" covered a group
 * that does not exist, a service that is down, and a server too old to have the
 * route. That last one cost a bug report: the endpoint 404'd because the
 * process predated it, and nothing on screen could distinguish that from a
 * group with no data.
 *
 * So the failure carries the status. Still no exception — the rule that a
 * picker is not worth a blank app holds — but the notice can now name the
 * difference between "unknown group" and "your server is out of date".
 */
export type GroupScreenResult =
  | { ok: true; snapshot: GroupSnapshot }
  | { ok: false; reason: string };

async function getJson<T>(path: string): Promise<T | undefined> {
  if (!SYM_URL) return undefined;
  try {
    const response = await fetch(`${SYM_URL}${path}`, { headers: await requestHeaders() });
    // Profile, fundamentals, news and names all come through here — the calls
    // a symbol change makes — so this is where a stale pass is noticed.
    notePassResponse(response);
    if (!response.ok) return undefined;
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}

/** A Strat month's Why tab: what decided it (qv-data `strategies/serve.py` `why`). */
export interface StrategyWhy {
  code: string;
  month: string;
  decidedOn: string;
  status: string;
  readings: Record<string, number>;
  margins: Record<string, number>;
  /** Margins under 0.5% — a rule that nearly flipped. */
  close: string[];
  /** Per stage, whether a later run answered differently. */
  changedSince: Record<string, boolean>;
}

/** The History strip: weight by fund (per cent) per month, and each month's return. */
export interface StrategyHistory {
  code: string;
  months: string[];
  funds: { symbol: string; weights: number[] }[];
  returns: (number | null)[];
  proxy: (boolean | null)[];
}

export function fetchStrategyWhy(code: string, month?: string): Promise<StrategyWhy | undefined> {
  const query = month ? `?month=${encodeURIComponent(month)}` : "";
  return getJson<StrategyWhy>(`/sym/strategies/${encodeURIComponent(code)}/why${query}`);
}

export function fetchStrategyHistory(code: string, months = 12): Promise<StrategyHistory | undefined> {
  return getJson<StrategyHistory>(`/sym/strategies/${encodeURIComponent(code)}/history?months=${months}`);
}

/** Group types with their counts, in the order the picker shows them. */
export function fetchGroupTypes(market?: string): Promise<SymbolGroupType[] | undefined> {
  const query = market ? `?market=${encodeURIComponent(market)}` : "";
  return getJson<SymbolGroupType[]>(`/sym/types${query}`);
}

/** Groups, optionally narrowed to one type. */
export function fetchGroups(
  groupType?: string,
  market?: string,
): Promise<SymbolGroup[] | undefined> {
  const params = new URLSearchParams();
  if (groupType) params.set("groupType", groupType);
  // Narrowing by market is what makes the picker coherent: SP500 and Nifty 50
  // in one list are not alternatives, and offering them as if they were is
  // asking a question nobody meant to answer.
  if (market) params.set("market", market);
  const query = params.toString();
  return getJson<SymbolGroup[]>(`/sym/groups${query ? `?${query}` : ""}`);
}

/**
 * One group as a decoded screener snapshot.
 *
 * The server emits the **same framed Arrow payload** as `/ws/scr`, so this
 * reuses the transport's decoder rather than parsing anything itself — which is
 * the whole reason a group-driven screen needs no grid changes.
 */
export async function fetchGroupScreen(
  code: string,
  /**
   * The columns the server has to materialise — `serverComputedColumns`' output,
   * the same array the screener socket sends.
   *
   * Without it a computed column was silently **blank** on every group: the
   * grid suppresses its `valueGetter` for a history-needing column because it
   * expects a real Arrow field, and this request never asked for one.
   */
  computed?: readonly unknown[],
  /**
   * Drop the server's cached frame and rebuild from the files on disk.
   *
   * Only ever the Refresh button. The server caches on the live snapshot and
   * the frame's last date, so a bar file rewritten under a running process —
   * the nightly ingest, or `universe_repair` closing a gap — changes no key and
   * is invisible until something says so. It costs a full frame rebuild, which
   * is why it is not what the ordinary re-push does.
   */
  refresh?: boolean,
  /**
   * Ticked sealed conditions, `{field, screen, id}` — a sibling of `computed`,
   * and omitted when empty for its reason. The socket path sends the same.
   */
  sealedConditions?: readonly unknown[],
  /** A Strat screen's month, `YYYY-MM` (phase 5); omitted is the newest. */
  strategyMonth?: string,
): Promise<GroupScreenResult> {
  return getFrames(`/sym/groups/${encodeURIComponent(code)}/screen`, (params) => {
    if (strategyMonth) params.set("month", strategyMonth);
    if (computed && computed.length > 0) params.set("computed", JSON.stringify(computed));
    if (sealedConditions && sealedConditions.length > 0) {
      params.set("sealedConditions", JSON.stringify(sealedConditions));
    }
    if (refresh) params.set("refresh", "true");
  });
}

/**
 * Several groups as one decoded snapshot.
 *
 * `or` is the union of their memberships, `and` the intersection. Sent to a
 * separate route rather than as a repeated `code` path param, because the
 * combined universe is not "a group" — it has no code, no stored member count
 * and nothing to 404 on — and shaping it as one would have made
 * `/sym/groups/{code}/screen` answer for a thing that has no `{code}`.
 *
 * **One code takes the single-group route**, which is not just an optimisation:
 * that path is the tested one, it is the only one that resolves a Trades group
 * against the journal, and a selection of one is exactly the request the bar has
 * always made. The multi route exists for the case that genuinely needs it.
 */
export function fetchGroupsScreen(
  codes: readonly string[],
  combine: "or" | "and",
  computed?: readonly unknown[],
  refresh?: boolean,
  sealedConditions?: readonly unknown[],
  strategyMonth?: string,
): Promise<GroupScreenResult> {
  if (codes.length === 0) {
    return Promise.resolve({ ok: false, reason: "no group selected" });
  }
  if (codes.length === 1) return fetchGroupScreen(codes[0]!, computed, refresh, sealedConditions, strategyMonth);
  return getFrames("/sym/screen", (params) => {
    params.set("groups", codes.join(","));
    params.set("mode", combine === "and" ? "intersect" : "union");
    if (computed && computed.length > 0) params.set("computed", JSON.stringify(computed));
    if (sealedConditions && sealedConditions.length > 0) {
      params.set("sealedConditions", JSON.stringify(sealedConditions));
    }
    if (refresh) params.set("refresh", "true");
  });
}

/**
 * The shared body of the two screen fetches: build the query, decode the frames.
 *
 * Both routes answer with the **same framed Arrow payload** as `/ws/scr`, so
 * both reuse the transport's decoder rather than parsing anything themselves —
 * which is the whole reason a group-driven screen needs no grid changes.
 */
async function getFrames(
  path: string,
  fill: (params: URLSearchParams) => void,
): Promise<GroupScreenResult> {
  if (!SYM_URL) return { ok: false, reason: "no symbol service configured" };
  try {
    // `userId` on every call, not only the trades ones: the server ignores it
    // for a group whose membership is the same for everybody, and a caller that
    // tried to guess which groups need it would be a second copy of that list.
    const params = new URLSearchParams({ userId: currentUserId() });
    fill(params);
    const response = await fetch(`${SYM_URL}${path}?${params.toString()}`, {
      headers: await requestHeaders(),
    });
    notePassResponse(response);
    if (!response.ok) {
      // The body, when there is one: every refusal this endpoint makes is a
      // sentence saying which group or which parameter was wrong, and dropping
      // it left the status code to explain things it cannot.
      const detail = await response.text().catch(() => "");
      return { ok: false, reason: failureReason(path, response.status, detail) };
    }
    const frames = decodeScreenerFrames(await response.arrayBuffer());
    return {
      ok: true,
      snapshot: {
        frames,
        detail: frames.find((f) => f.name === "detail"),
        columns: decodeColumnMeta(frames),
      },
    };
  } catch (err) {
    // A network error, or a body that would not decode. Named separately
    // because "cannot reach the server" and "the server said no" send a reader
    // to entirely different places.
    return {
      ok: false,
      reason: `cannot reach the symbol service (${err instanceof Error ? err.message : String(err)})`,
    };
  }
}

/**
 * Turn a status into something worth reading.
 *
 * The 404 is the one that matters and the one that is genuinely ambiguous:
 * FastAPI answers an unrouted path with `{"detail":"Not Found"}` while this
 * service answers an unknown group with a plain sentence, so the *shape* of the
 * body is what tells them apart. Guessing wrong is cheap; not guessing at all
 * cost a debugging session.
 */
function failureReason(path: string, status: number, body: string): string {
  const detail = body.trim().slice(0, 200);
  if (status === 404) {
    const routeMissing = detail === "" || detail.includes('"detail":"Not Found"');
    return routeMissing
      ? `the server has no ${path} — it may predate this feature and need a restart`
      : detail;
  }
  if (status === 400) return detail || "the request was refused";
  if (status === 429) return "too many requests — try again in a moment";
  if (status >= 500) return `the symbol service failed (${status})`;
  return detail || `request failed (${status})`;
}

/** One row of the client-side symbol index. */
export interface SymbolRow {
  symbol: string;
  name: string | null;
  sector: string | null;
  industry: string | null;
  marketCap: number | null;
  price: number | null;
  exchange: string | null;
  isEtf: boolean;
  /**
   * Which market the symbol **trades in** — `US`, `IN`.
   *
   * Not the issuer's domicile, which is what a `country` field would mean and
   * which is a different fact: ASML is Dutch and a dozen NASDAQ listings are
   * Indian. Derivable from the suffix (D33), and sent anyway so a row can be
   * badged and filtered without every consumer re-deriving it.
   */
  market: string | null;
}

/**
 * The whole universe, lean — fetched once on app load and searched locally.
 *
 * ~16,700 rows across both markets. Sent whole rather than queried per keystroke
 * because a symbol box has to feel instant, and a round trip per character is
 * the one thing that guarantees it will not.
 *
 * **Ordered by standing within a market, not by raw market cap.** A plain
 * `marketCap DESC` was right while there was one market and became wrong with
 * two, because the figure is in native currency: Reliance is 17.7e12 and Apple
 * 3.9e12, so a global sort puts most of the NSE above most of NASDAQ. The server
 * interleaves by rank instead — NVDA beside RELIANCE.NS as each venue's largest
 * — so a client that keeps list order still gets sane ranking for free, and
 * typing "A" still surfaces Apple first.
 *
 * `market` narrows to one; omitted returns both, which is what a search box
 * wants (D40: mix by identity, never by measure).
 */
export function fetchAllSymbols(market?: string): Promise<SymbolRow[] | undefined> {
  return getJson<SymbolRow[]>(market ? `/sym/symbols?market=${market}` : "/sym/symbols");
}

/**
 * Company names for the tickers already on screen — the toolbar's half of this.
 *
 * `fetchAllSymbols` is the *search* index: every row, because ranking happens
 * locally and a round trip per keystroke would never feel instant. A toolbar
 * label needs one name per chart, and making it pay for 14,000 rows is why it
 * only loaded on first focus — so the box read `AAPL` on a fresh page and
 * `AAPL - Apple Inc.` only after somebody clicked it.
 *
 * A ticker with no name we know is **absent from the answer**, not null. The
 * label falls back to the bare symbol either way, and absent is the one that
 * cannot be confused with a company literally named nothing.
 */
export function fetchSymbolNames(
  symbols: readonly string[],
): Promise<Record<string, string> | undefined> {
  const wanted = symbols.map((s) => s.trim().toUpperCase()).filter(Boolean);
  if (wanted.length === 0) return Promise.resolve({});
  return getJson<Record<string, string>>(`/sym/names?symbols=${encodeURIComponent(wanted.join(","))}`);
}

/**
 * What `GET /sym/profile/{symbol}` returns — two vendors merged into one record.
 *
 * Every field is optional, because neither vendor is a superset of the other
 * and a symbol with no profile at all (an index, a fund) is a legitimate answer
 * rather than a failure. `sources` maps each field to the vendor that supplied
 * it, so a panel can answer "why does this say 166,000" without a round trip.
 */
export interface CompanyProfile {
  symbol?: string;
  market?: string;
  name?: string;
  description?: string;
  sector?: string;
  industry?: string;
  /** Standard Industrial Classification — a regulator's label, not a vendor's. */
  sicCode?: string;
  sicDescription?: string;
  ceo?: string;
  employees?: number;
  website?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  exchangeCode?: string;
  exchangeName?: string;
  currency?: string;
  listedOn?: string;
  securityType?: string;
  marketCap?: number;
  sharesOutstanding?: number;
  sharesWeighted?: number;
  beta?: number;
  isin?: string;
  cusip?: string;
  cik?: string;
  figi?: string;
  logo?: string;
  yearRange?: string;
  lastDividend?: number;
  trading?: boolean;
  isEtf?: boolean;
  isFund?: boolean;
  isAdr?: boolean;
  sources?: Record<string, string>;
}

export function fetchCompanyProfile(symbol: string): Promise<CompanyProfile | undefined> {
  return getJson<CompanyProfile>(`/sym/profile/${encodeURIComponent(symbol)}`);
}

/** One metric's identity, as `fundamentals/metrics.py` declares it. */
export interface FundamentalMetric {
  id: string;
  label: string;
  description: string;
  unit: string;
  direction: string;
  category: string;
  precision: number;
  caveat?: string;
}

/** One fiscal year, or the trailing twelve months. */
export interface AnnualRow {
  /** `"2025"`, or `"TTM"` for the current trailing year. */
  label: string;
  periodEnd?: string;
  availableOn?: string;
  /** False where `availableOn` was reconstructed from a lag rather than filed. */
  pit?: boolean;
  [metric: string]: number | string | boolean | null | undefined;
}

/**
 * One reported quarter, as the chart marks it.
 *
 * The earnings half comes from the vendor's own rows and the revenue half from
 * our statements, joined server-side on the announcement date — so a record can
 * legitimately carry a full EPS surprise and no revenue at all, where the
 * announcement is older than the statements we hold. `epsSurprisePct` is a
 * **fraction**, not a percentage: `0.14` is the 14.00% the popup prints.
 */
export interface EarningsQuarter {
  /** The announcement date, `YYYY-MM-DD` — the bar the marker sits on. */
  date: string;
  /** The quarter it reported on, absent where no statement could be paired. */
  periodEnd?: string | null;
  epsActual?: number | null;
  epsEstimated?: number | null;
  epsSurprise?: number | null;
  epsSurprisePct?: number | null;
  revenue?: number | null;
  revenueEstimated?: number | null;
  revenueSurprise?: number | null;
  revenueSurprisePct?: number | null;
}

/** What `GET /sym/fundamentals/{symbol}` returns. */
export interface SymbolFundamentals {
  symbol: string;
  market: string;
  currency: string;
  /** The newest period's publication date — **not** its period end. */
  asOf?: string;
  periodEnd?: string;
  /** False where `asOf` was reconstructed from a lag rather than filed. */
  pit?: boolean;
  metrics: Record<string, number | string | boolean | null>;
  periods: Record<string, number | string | boolean | null>[];
  /**
   * The reported quarters, oldest first — what the chart's `E` markers are
   * placed from.
   *
   * Empty for a symbol outside the depth universe and for every Indian one:
   * the vendor's earnings endpoints carry no `.NS` rows at all. Absent rather
   * than an error, which is the same distinction `pit` draws.
   */
  earnings?: EarningsQuarter[];
  /**
   * One row per fiscal year, oldest first, the last labelled `TTM`.
   *
   * Every ratio recomputed for that year at the price that applied when the
   * year's figures became public — never at today's price, which inflates every
   * past multiple by all the growth since and flips the sign of a premium.
   */
  annual: AnnualRow[];
  depth: Record<string, number | string | null>;
  /**
   * `changePercentCc` — the move from the **previous close**, which is what a
   * peer list means by "Change". Not `changePercent`: that name now means the
   * move from today's open on every route, so the suffix is what says which of
   * the two a column carries.
   */
  peers: { symbol: string; close: number | null; changePercentCc: number | null }[];
  lenses: Record<string, string[]>;
  catalog: FundamentalMetric[];
}

/**
 * Everything known about one symbol's financials.
 *
 * `depth: false` skips the two per-symbol vendor fetches, which is what the PIP
 * wants — six rows at 170px have no room for a Piotroski score, and paying two
 * round trips to fill a panel nobody opened would be the wrong trade.
 */
export function fetchSymbolFundamentals(
  symbol: string,
  options: { depth?: boolean } = {},
): Promise<SymbolFundamentals | undefined> {
  const query = options.depth === false ? "?depth=false" : "";
  return getJson<SymbolFundamentals>(
    `/sym/fundamentals/${encodeURIComponent(symbol)}${query}`,
  );
}

/** One article, flattened by the server to what a panel renders. */
export interface NewsArticle {
  id: string;
  title: string;
  author: string | null;
  publishedUtc: string;
  url: string;
  imageUrl: string | null;
  publisher: string | null;
  publisherIcon: string | null;
  description: string | null;
  keywords: string[];
  /** `positive` | `neutral` | `negative`, **for this symbol** — not the article. */
  sentiment: string | null;
  /** Why the vendor read it that way, in words. */
  reasoning: string | null;
  /** The other tickers the article touches. */
  tickers: string[];
}

export interface NewsSentiment {
  counts: { positive: number; neutral: number; negative: number };
  total: number;
  /** Articles that took a side — neutrals excluded. */
  opinionated: number;
  /** Positives less negatives over the opinionated ones, or null for none. */
  net: number | null;
  latest: string | null;
}

/** The company's own announcement, as distinct from somebody writing about it. */
export interface PressRelease {
  title: string;
  publishedUtc: string;
  publisher: string | null;
  site: string | null;
  url: string;
  imageUrl: string | null;
  excerpt: string;
}

export interface SymbolNews {
  symbol: string;
  articles: NewsArticle[];
  sentiment: NewsSentiment;
  releases: PressRelease[];
}

/**
 * Recent coverage, its sentiment, and the company's own releases.
 *
 * One request feeds both the News and Sentiment views: the vendor attaches
 * sentiment to each article, so a second endpoint would be the same call twice.
 * `releases: false` drops the second vendor call, which is what a PIP wants.
 */
export function fetchSymbolNews(
  symbol: string,
  options: { limit?: number; releases?: boolean } = {},
): Promise<SymbolNews | undefined> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.releases === false) params.set("releases", "false");
  const query = params.toString();
  return getJson<SymbolNews>(
    `/sym/news/${encodeURIComponent(symbol)}${query ? `?${query}` : ""}`,
  );
}

/** One headline in a Movers News cell's list (`/sym/movers-news`). */
export interface MoversArticle {
  title: string | null;
  tag: string | null;
  /** Epoch ms. */
  at: number;
  publisher: string | null;
  url: string | null;
}

/**
 * The headlines a Movers News cell expands to: `symbol`'s articles in the
 * cell's window, from the server's cached market-wide feed — the same feed the
 * cell's `+N` was counted from, so the two agree. No vendor call.
 */
export async function fetchMoversNews(
  symbol: string,
  window: "pre" | "post" | "market",
): Promise<MoversArticle[] | undefined> {
  const body = await getJson<{ articles?: MoversArticle[] }>(
    `/sym/movers-news/${encodeURIComponent(symbol)}?window=${window}`,
  );
  return body ? (Array.isArray(body.articles) ? body.articles : []) : undefined;
}
